(() => {
  function initGallery(gallery) {
    if (!gallery || gallery.dataset.fpgGalleryInit === 'true') return;
    gallery.dataset.fpgGalleryInit = 'true';

    const slides = Array.from(gallery.querySelectorAll('[data-fpg-slide]'));
    const slideImages = Array.from(gallery.querySelectorAll('img'));
    const slideLinks = Array.from(gallery.querySelectorAll('a'));
    const prevButton = gallery.querySelector('[data-fpg-prev]');
    const nextButton = gallery.querySelector('[data-fpg-next]');
    const dotsHost = gallery.querySelector('[data-fpg-dots]');

    slideImages.forEach((img) => {
      img.setAttribute('draggable', 'false');
      img.addEventListener('dragstart', (event) => event.preventDefault());
    });
    slideLinks.forEach((link) => {
      link.setAttribute('draggable', 'false');
      link.addEventListener('dragstart', (event) => event.preventDefault());
    });
    if (slides.length <= 1) {
      if (prevButton) prevButton.hidden = true;
      if (nextButton) nextButton.hidden = true;
      if (dotsHost) dotsHost.hidden = true;
      return;
    }

    let activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (activeIndex < 0) activeIndex = 0;
    let isPointerDown = false;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let wheelLock = false;
    let suppressClick = false;
    let didDrag = false;
    let pressedLink = null;

    const dots = dotsHost
      ? slides.map((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'fpg-card-gallery__dot';
          dot.setAttribute('aria-label', `Show image ${index + 1}`);
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            setActive(index);
          });
          dotsHost.appendChild(dot);
          return dot;
        })
      : [];

    let hydrated = false;
    function hydrateSlides() {
      // Non-active slides ship with data-src/data-srcset/data-sizes (snippets/card-product.liquid)
      // so they are not fetched at load; promote them on the first sign of interaction.
      if (hydrated) return;
      hydrated = true;
      slides.forEach((slide) => {
        slide.querySelectorAll('img[data-srcset], img[data-src]').forEach((img) => {
          if (img.dataset.sizes) {
            img.setAttribute('sizes', img.dataset.sizes);
            delete img.dataset.sizes;
          }
          if (img.dataset.srcset) {
            img.setAttribute('srcset', img.dataset.srcset);
            delete img.dataset.srcset;
          }
          if (img.dataset.src) {
            img.setAttribute('src', img.dataset.src);
            delete img.dataset.src;
          }
        });
      });
    }

    function setActive(index) {
      if (index !== activeIndex) hydrateSlides();
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === activeIndex);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === activeIndex);
      });
    }

    function handleSwipe(deltaX, deltaY) {
      const SWIPE_THRESHOLD = 36;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD) return false;
      if (Math.abs(deltaY) > Math.abs(deltaX)) return false;
      if (deltaX < 0) {
        setActive(activeIndex + 1);
      } else {
        setActive(activeIndex - 1);
      }
      return true;
    }

    function onPointerDown(event) {
      pressedLink = null;
      if (event.button !== undefined && event.button !== 0) return;
      if (event.target.closest('[data-fpg-prev], [data-fpg-next], .fpg-card-gallery__dot')) return;
      hydrateSlides();
      isPointerDown = true;
      didDrag = false;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      pressedLink = event.target.closest('a.fpg-card-gallery__image-link, a.card__media-link');
      gallery.setPointerCapture?.(event.pointerId);
    }

    function onPointerMove(event) {
      if (!isPointerDown) return;
      const deltaX = event.clientX - pointerStartX;
      const deltaY = event.clientY - pointerStartY;
      if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
        didDrag = true;
        // Keep horizontal swipe smooth and avoid accidental link activation.
        event.preventDefault();
      }
    }

    function onPointerUp(event) {
      if (!isPointerDown) return;
      isPointerDown = false;
      gallery.releasePointerCapture?.(event.pointerId);
      const deltaX = event.clientX - pointerStartX;
      const deltaY = event.clientY - pointerStartY;
      const didSwipe = handleSwipe(deltaX, deltaY);
      if (didSwipe || didDrag) {
        // Prevent synthetic click from opening product link right after a swipe.
        suppressClick = true;
        event.preventDefault();
        event.stopPropagation();
        window.setTimeout(() => {
          suppressClick = false;
        }, 180);
        pressedLink = null;
        return;
      }

      // Pointer capture can prevent native anchor click targeting.
      // For true click/tap (no drag/swipe), navigate explicitly.
      if (pressedLink && pressedLink.href) {
        window.location.assign(pressedLink.href);
      }
      pressedLink = null;
    }

    function onPointerCancel() {
      isPointerDown = false;
      didDrag = false;
      pressedLink = null;
    }

    function onWheel(event) {
      const absX = Math.abs(event.deltaX);
      const absY = Math.abs(event.deltaY);
      const horizontalIntent = absX > absY && absX > 10;
      if (!horizontalIntent || wheelLock) return;

      hydrateSlides();
      wheelLock = true;
      window.setTimeout(() => {
        wheelLock = false;
      }, 220);

      event.preventDefault();
      if (event.deltaX > 0) {
        setActive(activeIndex + 1);
      } else {
        setActive(activeIndex - 1);
      }
    }

    prevButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setActive(activeIndex - 1);
    });
    nextButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setActive(activeIndex + 1);
    });
    gallery.addEventListener('pointerenter', hydrateSlides, { once: true, passive: true });
    gallery.addEventListener('touchstart', hydrateSlides, { once: true, passive: true });
    gallery.addEventListener('pointerdown', onPointerDown);
    gallery.addEventListener('pointermove', onPointerMove);
    gallery.addEventListener('pointerup', onPointerUp);
    gallery.addEventListener('pointercancel', onPointerCancel);
    gallery.addEventListener('pointerleave', onPointerCancel);
    gallery.addEventListener('wheel', onWheel, { passive: false });
    gallery.addEventListener(
      'click',
      (event) => {
        if (suppressClick) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    setActive(activeIndex);
  }

  function initAll(root = document) {
    const galleries = root.querySelectorAll('.fpg-card-gallery[data-fpg-card-gallery]');
    galleries.forEach(initGallery);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('fpg:gallery:init', (event) => initAll(event.target || document));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches('.fpg-card-gallery[data-fpg-card-gallery]')) {
          initGallery(node);
          return;
        }
        initAll(node);
      });
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    // Only watch grid roots; observing document.body ran querySelectorAll on every node any
    // app embed or the cart drawer inserted during load. facets.js dispatches fpg:gallery:init
    // after AJAX filtering, and shopify:section:load covers the theme editor.
    document.querySelectorAll('#ProductGridContainer, .fancy-product-grid').forEach((root) => {
      observer.observe(root, { childList: true, subtree: true });
    });
  });
})();
